import { useEffect, useCallback, useRef, type MutableRefObject } from 'react';
import type { FieldValues, UseFormReset, UseFormWatch } from 'react-hook-form';

const DRAFT_VERSION = 1;
const MAX_DRAFT_AGE_MS = 24 * 60 * 60 * 1000;

type DraftEnvelope<T> = {
  v: typeof DRAFT_VERSION;
  savedAt: number;
  values: T;
};

/**
 * Builds the sessionStorage key for form draft persistence
 * Centralized key derivation to ensure consistency across the codebase
 */
export function getFormDraftStorageKey(
  formType: string,
  accountId: string | null,
  entityId: string | null = null
): string {
  return `vester:portfolio:formDraft:${formType}:${accountId || 'new'}:${entityId || 'new'}`;
}

export function writeFormDraft<T extends FieldValues>(storageKey: string, values: T): void {
  if (typeof window === 'undefined') return;
  const envelope: DraftEnvelope<T> = {
    v: DRAFT_VERSION,
    savedAt: Date.now(),
    values,
  };
  sessionStorage.setItem(storageKey, JSON.stringify(envelope));
}

function readFormDraft<T extends FieldValues>(storageKey: string): T | null {
  const saved = sessionStorage.getItem(storageKey);
  if (!saved) return null;

  const parsed = JSON.parse(saved);
  if (!parsed || typeof parsed !== 'object') return null;

  if ('v' in parsed || 'savedAt' in parsed || 'values' in parsed) {
    if (
      parsed.v !== DRAFT_VERSION ||
      typeof parsed.savedAt !== 'number' ||
      Date.now() - parsed.savedAt > MAX_DRAFT_AGE_MS ||
      !parsed.values ||
      typeof parsed.values !== 'object'
    ) {
      sessionStorage.removeItem(storageKey);
      return null;
    }

    return parsed.values as T;
  }

  // Gracefully support drafts saved before the versioned envelope existed.
  return parsed as T;
}

/**
 * useFormDraftPersistence
 * 
 * Custom hook for persisting React Hook Form drafts to sessionStorage.
 * Industry-standard approach: uses watch() + debounced writes, restores via reset().
 * 
 * @param formType - Unique identifier for the form type (e.g., 'manualTransaction', 'addManualAccount')
 * @param accountId - Account ID for keying the draft (null for new accounts)
 * @param watch - React Hook Form watch function
 * @param reset - React Hook Form reset function
 * @param options - Configuration options
 */
export function useFormDraftPersistence<T extends FieldValues>(
  formType: string,
  accountId: string | null,
  watch: UseFormWatch<T>,
  reset: UseFormReset<T>,
  options?: {
    enabled?: boolean;
    entityId?: string | null;
    isRestoringRef?: MutableRefObject<boolean>;
  }
) {
  const { enabled = true, entityId = null, isRestoringRef } = options || {};
  const storageKey = getFormDraftStorageKey(formType, accountId, entityId);
  const didRestoreRef = useRef(false);

  // Restore on mount.
  useEffect(() => {
    didRestoreRef.current = false;
    if (typeof window === 'undefined' || !enabled) return;
    
    try {
      const values = readFormDraft<T>(storageKey);
      if (values) {
        didRestoreRef.current = true;
        if (isRestoringRef) {
          isRestoringRef.current = true;
        }
        reset(values);
        window.setTimeout(() => {
          if (isRestoringRef) {
            isRestoringRef.current = false;
          }
        }, 0);
      }
    } catch (e) {
      console.error('Failed to restore form draft:', e);
      try {
        sessionStorage.removeItem(storageKey);
      } catch {}
      if (isRestoringRef) {
        isRestoringRef.current = false;
      }
    }
  }, [storageKey, enabled, reset, isRestoringRef]);

  // Debounced save using React Hook Form's subscription API.
  useEffect(() => {
    if (typeof window === 'undefined' || !enabled) return;
    
    let timer: ReturnType<typeof setTimeout> | null = null;
    const subscription = watch((formValues) => {
      if (isRestoringRef?.current) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        try {
          writeFormDraft(storageKey, formValues as T);
        } catch (e) {
          console.error('Failed to save form draft:', e);
        }
      }, 500);
    });
    
    return () => {
      if (timer) clearTimeout(timer);
      subscription.unsubscribe();
    };
  }, [watch, storageKey, enabled, isRestoringRef]);

  // Clear function
  const clearDraft = useCallback(() => {
    if (typeof window === 'undefined') return;
    sessionStorage.removeItem(storageKey);
  }, [storageKey]);

  return { clearDraft, didRestoreRef };
}
